import React, { useState, useRef, useEffect } from "react";
import propTypes from "prop-types";
import Icon from "../icons/Icon";
import classnames from "classnames";

import "./LoadingIndicator.scss";
import { createDataAttributes } from "../../utilities";
import { CSSTransition } from "react-transition-group";

const LoadingIndicator = ({
  message,
  type,
  color,
  className,
  dataAttrib,
  show,
  duration,
  animateOut,
  loadTo100,
  onEndOfLoadAnimation,
  onRemovedFromDom,
  freezeEmptyBar,
  ...rest
}) => {
  const [fullProgress, setFullProgress] = useState(false);

  const classes = classnames(
    className,
    "uikit__loading-message",
    "uikit__loading-message__" + color,
    "uikit__loading-message__" + type,
    "uikit__loading-indicator",
    { "uikit__loading-indicator__full": fullProgress && animateOut }
  );

  const styles =
    !fullProgress && duration
      ? {
          animationDuration: `${duration}s`
        }
      : {};

  const tymeout = useRef(null);
  const prevShow = useRef(null);

  /**
   * this useEffect resets the tymeout reference after the animation is
   * completed... to cover the scenario where the component may be re-used
   * ... any time the `show` prop changes, we should reset the timeout tracker
   */
  useEffect(() => {
    if (show !== prevShow.current) {
      tymeout.current = null;
      prevShow.current = show;
    }
  });

  /**
   * this useEffect triggers the `onEndOfLoadAnimation` at the end of the desired
   * animation duration
   */
  useEffect(() => {
    if (
      onEndOfLoadAnimation &&
      show &&
      duration &&
      tymeout.current !== "completed" &&
      !freezeEmptyBar
    )
      tymeout.current = setTimeout(() => {
        onEndOfLoadAnimation();
        /**
         * once the animation is done, mark the timeout tracker as completed
         * ... this prevent it from any attempts to retrigger on new renders
         */
        tymeout.current = "completed";
      }, duration * 1000);

    return () => {
      if (tymeout?.current && tymeout?.current !== "completed")
        clearTimeout(tymeout.current);
    };
  }, [freezeEmptyBar, show, duration, onEndOfLoadAnimation]);

  return (
    <CSSTransition
      in={show}
      timeout={1000}
      unmountOnExit
      onEnter={() => setFullProgress(false)}
      onExiting={() => setFullProgress(true)}
      onExited={() => {
        if (onRemovedFromDom) onRemovedFromDom();
      }}
    >
      <span className={classes} {...rest} {...createDataAttributes(dataAttrib)}>
        <span className="uikit__loading-message__text">{message}</span>
        {type === "bar" && (
          <span className="uikit__loading-message__bar__wrapper">
            <span className="uikit__loading-message__bar__color uikit__loading-message__bar__infinite"></span>
          </span>
        )}

        {type === "progress" && (
          <span className="uikit__loading-message__bar__wrapper">
            <span
              style={styles}
              className={classnames(
                "uikit__loading-message__bar__color uikit__loading-message__bar__once",
                {
                  "uikit__loading-message__bar__color uikit__loading-message__bar__once__freezeEmpty":
                    freezeEmptyBar,
                  "uikit__loading-message__bar__color uikit__loading-message__bar__once__stopAt98":
                    !loadTo100 && !freezeEmptyBar,
                  "uikit__loading-message__bar__color uikit__loading-message__bar__once__stopAt100":
                    loadTo100 && !freezeEmptyBar,
                  "uikit__loading-message__bar__full-progress": fullProgress,
                  "uikit__loading-message__bar__full-progress__animate-out":
                    !show && fullProgress
                }
              )}
            ></span>
          </span>
        )}

        {type === "spinner" && (
          <span className="uikit__loading-message__spinner-wrapper">
            <Icon icon="farSpinnerThird" />
          </span>
        )}
      </span>
    </CSSTransition>
  );
};

LoadingIndicator.propTypes = {
  message: propTypes.string,
  type: propTypes.oneOf(["bar", "spinner", "progress"]),
  color: propTypes.oneOf(["grey", "blue", "orange", "green", "black"]),
  className: propTypes.string,
  dataAttrib: propTypes.arrayOf(Object),
  duration: propTypes.number,
  show: propTypes.bool,
  animateOut: propTypes.bool,
  loadTo100: propTypes.bool,
  onEndOfLoadAnimation: propTypes.func,
  onRemovedFromDom: propTypes.func,
  freezeEmptyBar: propTypes.bool
};
LoadingIndicator.defaultProps = {
  message: "Loading...",
  type: "bar",
  color: "grey",
  duration: 5,
  show: true,
  animateOut: true,
  loadTo100: false,
  freezeEmptyBar: false
};
export default LoadingIndicator;
